const CACHE_NAME = 'text2-images';
const HASH_STORAGE_KEY = 'text2-image-hashes';
const MANIFEST_URL = '/api/image-manifest';
const CONCURRENCY = 8;
const MATCH_OPTIONS = { ignoreVary: true };
const SW_CONTROL_TIMEOUT_MS = 1500;

let preloadPromise = null;
let progressListeners = new Set();

function readStoredHashes() {
  try {
    const raw = localStorage.getItem(HASH_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStoredHashes(hashes) {
  try {
    localStorage.setItem(HASH_STORAGE_KEY, JSON.stringify(hashes));
  } catch {
    // Quota or private mode — preload still works without persisted hashes.
  }
}

function notifyProgress(progress) {
  progressListeners.forEach((listener) => {
    try {
      listener(progress);
    } catch {
      // ignore listener errors
    }
  });
}

function subscribeProgress(listener) {
  progressListeners.add(listener);
  return () => progressListeners.delete(listener);
}

async function openImageCache() {
  if (typeof caches === 'undefined') {
    return null;
  }
  try {
    return await caches.open(CACHE_NAME);
  } catch {
    return null;
  }
}

function absoluteUrl(url) {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}${url.startsWith('/') ? url : `/${url}`}`;
}

function toCacheableResponse(response) {
  const headers = new Headers(response.headers);
  headers.delete('Vary');
  headers.set('Cache-Control', 'public, max-age=31536000, immutable');
  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
}

function decodeImageFromBlob(blobUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = blobUrl;
  });
}

async function warmImageFromCacheEntry(cache, url) {
  const cached = await cache.match(absoluteUrl(url), MATCH_OPTIONS);
  if (!cached) {
    return false;
  }
  try {
    const blob = await cached.blob();
    const blobUrl = URL.createObjectURL(blob);
    const ok = await decodeImageFromBlob(blobUrl);
    URL.revokeObjectURL(blobUrl);
    return ok;
  } catch {
    return false;
  }
}

async function fetchAndCacheImage(cache, url, hash) {
  const fullUrl = absoluteUrl(url);
  // Görseller aynı adla güncellenebiliyor; CDN (Cloudflare) eski kopyayı uzun süre
  // tutabildiği için içerik hash'i sorgu olarak eklenir. Önbellekte yine adıyla saklanır.
  const fetchUrl = hash ? `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(hash)}` : fullUrl;
  try {
    const response = await fetch(fetchUrl, { cache: 'reload', mode: 'same-origin' });
    if (!response.ok) {
      return false;
    }
    if (cache) {
      try {
        await cache.put(fullUrl, toCacheableResponse(response.clone()));
      } catch {
        // Quota or private mode.
      }
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const ok = await decodeImageFromBlob(blobUrl);
    URL.revokeObjectURL(blobUrl);
    return ok;
  } catch {
    return false;
  }
}

async function pruneStaleCacheEntries(cache, manifestUrls) {
  if (!cache) return;
  try {
    const keys = await cache.keys();
    await Promise.all(
      keys.map(async (request) => {
        const pathname = new URL(request.url).pathname;
        if (!manifestUrls.has(pathname)) {
          await cache.delete(request);
        }
      }),
    );
  } catch {
    // ignore prune failures
  }
}

async function runPool(items, worker, concurrency) {
  let index = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (index < items.length) {
      const current = index;
      index += 1;
      await worker(items[current], current);
    }
  });
  await Promise.all(runners);
}

async function preloadAllImagesInternal() {
  const response = await fetch(MANIFEST_URL);
  if (!response.ok) {
    throw new Error(`Image manifest failed: ${response.status}`);
  }

  const data = await response.json();
  const files = Array.isArray(data?.files) ? data.files : [];
  const total = files.length;
  let loaded = 0;

  const report = () => {
    const progress = { loaded, total };
    notifyProgress(progress);
  };

  report();

  if (total === 0) {
    return { loaded: 0, total: 0 };
  }

  const cache = await openImageCache();
  const storedHashes = readStoredHashes();
  const nextHashes = {};
  const manifestUrls = new Set(files.map((file) => file.url));

  await pruneStaleCacheEntries(cache, manifestUrls);

  await runPool(
    files,
    async (file) => {
      const { url, hash } = file;
      nextHashes[url] = hash;

      const hashMatches = storedHashes[url] === hash;
      let success = false;

      if (hashMatches && cache) {
        success = await warmImageFromCacheEntry(cache, url);
      }

      if (!success) {
        success = await fetchAndCacheImage(cache, url, hash);
      }

      loaded += 1;
      report();

      if (!success) {
        // Count as loaded even if a single asset fails.
      }
    },
    CONCURRENCY,
  );

  writeStoredHashes(nextHashes);
  return { loaded, total };
}

function waitForServiceWorkerController() {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
    return Promise.resolve();
  }
  if (navigator.serviceWorker.controller) {
    return Promise.resolve();
  }
  return Promise.race([
    new Promise((resolve) => {
      navigator.serviceWorker.addEventListener('controllerchange', resolve, { once: true });
    }),
    new Promise((resolve) => {
      setTimeout(resolve, SW_CONTROL_TIMEOUT_MS);
    }),
  ]);
}

export function registerImageCacheServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null);
  }
  const swUrl = `${process.env.PUBLIC_URL || ''}/image-cache-sw.js`;
  return navigator.serviceWorker
    .register(swUrl)
    .then(async (registration) => {
      await navigator.serviceWorker.ready;
      await waitForServiceWorkerController();
      return registration;
    })
    .catch(() => null);
}

export function startImagePreload(onProgress) {
  if (onProgress) {
    subscribeProgress(onProgress);
  }

  if (!preloadPromise) {
    preloadPromise = (async () => {
      const swReady = registerImageCacheServiceWorker();
      const result = await preloadAllImagesInternal();
      await swReady;
      return result;
    })().catch((err) => {
      preloadPromise = null;
      throw err;
    });
  }

  return preloadPromise;
}

export function getImagePreloadPromise() {
  return preloadPromise;
}

export { subscribeProgress as subscribeImagePreloadProgress };
